import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { get, post } from '../../api/client';
import type { PaymentTransactionRow } from '../payment/types';

const STATUS_TABS = [
  { label: '待确认', value: 'pending' },
  { label: '已确认', value: 'success' },
  { label: '已驳回', value: 'closed' },
  { label: '全部', value: 'all' },
];

function statusTag(status: string) {
  switch (status) {
    case 'pending':
      return <Tag color="warning">待确认</Tag>;
    case 'success':
      return <Tag color="success">已确认</Tag>;
    case 'closed':
      return <Tag>已驳回</Tag>;
    case 'failed':
      return <Tag color="error">失败</Tag>;
    default:
      return <Tag>{status}</Tag>;
  }
}

/**
 * 银行转账人工审批页。
 * - GET /admin/payment-transactions?provider=bank_transfer 拉取转账流水（含订单摘要）。
 * - 用户线下汇款、无上传凭证环节；核对银行实际到账与金额后「确认到账」→ POST .../:id/confirm 触发履约发放会员；
 *   「驳回」→ POST .../:id/reject 关闭订单并释放优惠名额。
 */
export default function PaymentTransfersPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [confirming, setConfirming] = useState<PaymentTransactionRow | null>(null);
  const [rejecting, setRejecting] = useState<PaymentTransactionRow | null>(null);
  const [note, setNote] = useState('');
  const [rejectForm] = Form.useForm();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['payment-transactions', 'bank_transfer', status],
    queryFn: () =>
      get<PaymentTransactionRow[]>('/admin/payment-transactions', {
        provider: 'bank_transfer',
        ...(status === 'all' ? {} : { status }),
        pageSize: 200,
      }),
  });
  const rows = data ?? [];

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['payment-transactions'] });

  const confirm = useMutation({
    mutationFn: ({ id, note: n }: { id: string; note?: string }) =>
      post(`/admin/payment-transactions/${id}/confirm`, n ? { note: n } : {}),
    onSuccess: () => {
      message.success('已确认到账并发放会员');
      setConfirming(null);
      setNote('');
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '确认失败'),
  });

  const reject = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post(`/admin/payment-transactions/${id}/reject`, { reason }),
    onSuccess: () => {
      message.success('已驳回该转账');
      setRejecting(null);
      rejectForm.resetFields();
      invalidate();
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '驳回失败'),
  });

  const columns: ColumnsType<PaymentTransactionRow> = [
    { title: '订单号', dataIndex: 'orderNo', width: 190, render: (v: string | null) => v || '-' },
    {
      title: '用户',
      dataIndex: 'userId',
      width: 110,
      render: (v: string | null) => (v ? <Typography.Text code>{v.slice(0, 8)}</Typography.Text> : '-'),
    },
    {
      title: '转账金额',
      dataIndex: 'amount',
      width: 120,
      render: (v: number, r) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>¥{Number(v).toFixed(2)}</Typography.Text>
          {r.orderAmount != null && Number(r.orderAmount) !== Number(v) && (
            <Typography.Text type="danger" style={{ fontSize: 11 }}>
              订单应付 ¥{Number(r.orderAmount).toFixed(2)}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    { title: '状态', dataIndex: 'status', width: 96, render: (v: string) => statusTag(v) },
    {
      title: '提交时间',
      dataIndex: 'createdAt',
      width: 170,
      render: (v: string) => (v ? new Date(v).toLocaleString('zh-CN') : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_, r) =>
        r.status === 'pending' ? (
          <Space>
            <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => setConfirming(r)}>
              确认到账
            </Button>
            <Button size="small" danger icon={<CloseOutlined />} onClick={() => setRejecting(r)}>
              驳回
            </Button>
          </Space>
        ) : (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {r.status === 'success' ? `已于 ${r.confirmAt ? new Date(r.confirmAt).toLocaleString('zh-CN') : '-'}` : r.rejectReason || '已处理'}
          </Typography.Text>
        ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          银行转账审批
        </Typography.Title>
        <Typography.Text type="secondary">
          用户选择银行转账后线下汇款（无上传凭证环节），请核对银行实际到账后确认（自动发放会员）或驳回（关闭订单并释放优惠名额）。
        </Typography.Text>
      </div>

      <Card styles={{ body: { paddingTop: 16 } }}>
        <Space style={{ marginBottom: 16 }}>
          <Segmented options={STATUS_TABS} value={status} onChange={(v) => setStatus(v as string)} />
          {isFetching && !isLoading && <Typography.Text type="secondary">刷新中…</Typography.Text>}
        </Space>

        <Table<PaymentTransactionRow>
          rowKey="id"
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: 960 }}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          locale={{ emptyText: '暂无银行转账流水' }}
        />
      </Card>

      {/* 确认到账 */}
      <Modal
        title="确认转账到账"
        open={confirming !== null}
        onCancel={() => {
          setConfirming(null);
          setNote('');
        }}
        onOk={() => confirming && confirm.mutate({ id: confirming.id, note: note.trim() || undefined })}
        okText="确认到账并发放会员"
        cancelText="取消"
        confirmLoading={confirm.isPending}
      >
        {confirming && (
          <>
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="订单号">{confirming.orderNo || '-'}</Descriptions.Item>
              <Descriptions.Item label="转账金额">¥{Number(confirming.amount).toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label="订单应付">
                {confirming.orderAmount != null ? `¥${Number(confirming.orderAmount).toFixed(2)}` : '-'}
              </Descriptions.Item>
            </Descriptions>
            {confirming.orderAmount != null && Number(confirming.orderAmount) !== Number(confirming.amount) && (
              <Typography.Paragraph type="danger" style={{ fontSize: 12 }}>
                注意：转账金额与订单应付金额不一致，请核实后再确认。
              </Typography.Paragraph>
            )}
            <Form layout="vertical">
              <Form.Item label="备注（选填）">
                <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="如：已核对到账流水号" />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>

      {/* 驳回 */}
      <Modal
        title="驳回转账"
        open={rejecting !== null}
        onCancel={() => {
          setRejecting(null);
          rejectForm.resetFields();
        }}
        onOk={async () => {
          try {
            const v = await rejectForm.validateFields();
            if (rejecting) reject.mutate({ id: rejecting.id, reason: v.reason });
          } catch {
            /* 校验失败 */
          }
        }}
        okText="确认驳回"
        okButtonProps={{ danger: true }}
        cancelText="取消"
        confirmLoading={reject.isPending}
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          驳回后订单将关闭，并释放下单时预留的优惠名额。请填写驳回原因（用户可见）。
        </Typography.Paragraph>
        <Form form={rejectForm} layout="vertical">
          <Form.Item
            name="reason"
            label="驳回原因"
            rules={[{ required: true, whitespace: true, message: '请填写驳回原因' }]}
          >
            <Input.TextArea rows={3} placeholder="如：银行未查到到账流水 / 金额不符 / 非本人转账" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
